/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2022 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2022 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import { Box, Card, LinearProgress } from '@mui/material';
import React from 'react';
import { AlertBanner } from '@opengeoweb/shared';
import type { CapPresets } from '../types';
import { WarningListRowCapConnect } from './WarningListRowCap/WarningListRowCapConnect';
import type { CapAlert } from '../../store/types';
import { useCapTranslation } from '../../utils/i18n';

interface WarningListCapProps {
  capWarningPresets: CapPresets;
  alerts: CapAlert[];
  isPending?: boolean;
}

export const WarningListCap: React.FC<WarningListCapProps> = ({
  capWarningPresets,
  alerts,
  isPending = false,
}) => {
  const { t } = useCapTranslation();

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex' }}>
      {isPending ? (
        <LinearProgress
          color="secondary"
          sx={{ position: 'absolute', width: '100%' }}
        />
      ) : null}

      <Card
        data-testid="warningList"
        sx={{
          justifyContent: 'center',
          alignItems: 'center',
          overflow: 'auto',
          height: '95%',
          width: '100%',
          padding: '10px',
          margin: '20px',
          marginBottom: '10px',
          position: 'relative',
        }}
      >
        {!isPending && !alerts.length && (
          <AlertBanner severity="warning" title="No results" />
        )}
        {alerts.length > 0 && (
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <AlertBanner
              severity="info"
              title={t('warning-list-cap-alert-banner-title')}
            />
          </Box>
        )}
        {alerts.map((alert) => (
          <WarningListRowCapConnect
            alert={alert}
            capWarningPresets={capWarningPresets!}
            key={alert.identifier}
          />
        ))}
      </Card>
    </Box>
  );
};
